import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, IconButton, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { m, AnimatePresence } from 'framer-motion';
import { COLORS } from './styles';

// MultiSelectTopBar — fixed bar shown when the operator long-presses a
// bubble and enters multi-select mode. Slides in from the top.
//
// Uses framer-motion `m.div` (NOT `motion.div`) because the app wraps the
// tree in a strict-mode `LazyMotion` provider — `motion.*` throws an
// invariant. See shared-infra commit 58daa2be.
//
// Actions are render-prop-style so the parent owns business logic. The
// bar itself only renders the count + buttons; clicks delegate up.
const MultiSelectTopBar = ({
    open,
    count,
    onCancel,
    onForward,
    onCopy,
    onStar,
    onDelete,
    starDisabled = false,
}) => (
    <AnimatePresence>
        {open ? (
            <m.div
                key="wa-multiselect-bar"
                initial={{ y: -56, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -56, opacity: 0 }}
                transition={{ duration: 0.18, ease: [0.2, 0.9, 0.3, 1] }}
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 30,
                    width: '100%',
                }}
            >
                <Box
                    role="toolbar"
                    aria-label={`Multi-select toolbar, ${count} message${count === 1 ? '' : 's'} selected`}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: COLORS.primary,
                        color: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
                    }}
                >
                    <Tooltip title="Cancel selection" arrow>
                        <IconButton
                            size="small"
                            onClick={onCancel}
                            aria-label="Cancel selection"
                            sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } }}
                        >
                            <Icon icon="mdi:close" width={20} height={20} />
                        </IconButton>
                    </Tooltip>
                    <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                        {`${count} selected`}
                    </Typography>
                    <Tooltip title="Forward" arrow>
                        <IconButton
                            size="small"
                            onClick={onForward}
                            disabled={count === 0}
                            aria-label="Forward selected messages"
                            sx={{
                                color: '#FFFFFF',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.45)' },
                            }}
                        >
                            <Icon icon="mdi:share-outline" width={20} height={20} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy" arrow>
                        <IconButton
                            size="small"
                            onClick={onCopy}
                            disabled={count === 0}
                            aria-label="Copy selected messages"
                            sx={{
                                color: '#FFFFFF',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.45)' },
                            }}
                        >
                            <Icon icon="mdi:content-copy" width={20} height={20} />
                        </IconButton>
                    </Tooltip>
                    {/* Star is gated by Track B's thunk — the parent passes
                        starDisabled=true when the thunk isn't wired yet. */}
                    <Tooltip title={starDisabled ? 'Star unavailable' : 'Star'} arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={onStar}
                                disabled={starDisabled || count === 0}
                                aria-label="Star selected messages"
                                sx={{
                                    color: '#FFFFFF',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.45)' },
                                }}
                            >
                                <Icon icon="mdi:star-outline" width={20} height={20} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                        <IconButton
                            size="small"
                            onClick={onDelete}
                            disabled={count === 0}
                            aria-label="Delete selected messages"
                            sx={{
                                color: '#FFFFFF',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.45)' },
                            }}
                        >
                            <Icon icon="mdi:delete-outline" width={20} height={20} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </m.div>
        ) : null}
    </AnimatePresence>
);

MultiSelectTopBar.propTypes = {
    open: PropTypes.bool.isRequired,
    count: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    onForward: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
    onStar: PropTypes.func,
    onDelete: PropTypes.func.isRequired,
    starDisabled: PropTypes.bool,
};

export default MultiSelectTopBar;
